import Vue from 'vue';
import Router from 'vue-router';
import legacyNavigation from './utils/legacyNavigation';
import axios from 'axios';
import Cookies from 'js-cookie';
import RouterLinkLocaled from '@/components/RouterLinkLocaled';
import supportLocale from '@/../server/constants/supportLocale';
import {
  isValidLang,
  isValidDisabledLang,
  getUserLang,
} from '@/../server/utils/LanguageHelper';
import scrollBehavior from '@/router/utils/scrollBehavior';
import { createMainRoutes } from './routers/mainRouter';
import FooterArticleRouter from './routers/footerArticleRouter';

/**
 * Pre-setting rotuer information for 'SIGHTMAP' page,
 * 
 * TOUR_CATEGORY - 'suggested-tour' etc.
 * EXPERIENCE_CATEGORY - 'sightseeing', 'shows-and-entertainment' etc.
 */
import SIGHT_MAP_TYPE from '@/constants/sightMapType';
import TOUR_CATEGORY from '@/constants/tourCategory';
import EXPERIENCE_CATEGORY from '@/constants/experienceCategory';
import CreateCustomArticle from '@/views/CreateCustomArticleView';

/**
 * Page Import
 */
const SightMapView = () => import(/* webpackChunkName: "s" */ '@/views/SightMapView');
const createSightMapOverviewView = (sightMapType, category, categoryPath=null) => () => 
  import(/* webpackChunkName: "so" */ '@/views/CreateSightMapOverviewView')
    .then(m => m.default(sightMapType, category, categoryPath));
const createSightMapListView = (sightMapType, category, categoryPath=null) => () => 
  import(/* webpackChunkName: "sl" */ '@/views/CreateSightMapListView')
    .then(m => m.default(sightMapType, category, categoryPath));
const createSightMapSingleView = (sightMapType, category, categoryPath=null) => () => 
  import(/* webpackChunkName: "ss" */ '@/views/CreateSightMapSingleView')
    .then(m => m.default(sightMapType, category, categoryPath));
const SightView = () => import(/* webpackChunkName: "sv" */'@/views/SightView');
const TagView = () => import('@/views/TagView');

import AccommodationSightMapRouter from './routers/accommodationSightMapRouter';
import EventSightMapRouter from './routers/eventSightMapRouter';
import DiningSightMapRouter from './routers/diningSightMapRouter';

const WhoopsView = () => import(/* webpackChunkName: "oops" */'@/views/WhoopsView');
const PageNotFoundView = () => import(/* webpackChunkName: "404" */'@/views/PageNotFoundView');

/**
 * Import Language Wrapper
 * 
 * wrapper include language specific styles
 */
const arWrapper = () => import(/* webpackChunkName: "ar.localized" */ '@/localeWrapper/arWrapper');
const deWrapper = () => import(/* webpackChunkName: "de.localized" */ '@/localeWrapper/deWrapper');
const enWrapper = () => import(/* webpackChunkName: "en.localized" */ '@/localeWrapper/enWrapper');
const esWrapper = () => import(/* webpackChunkName: "es.localized" */ '@/localeWrapper/esWrapper');
const frWrapper = () => import(/* webpackChunkName: "fr.localized" */ '@/localeWrapper/frWrapper');
const idWrapper = () => import(/* webpackChunkName: "id.localized" */ '@/localeWrapper/idWrapper');
const itWrapper = () => import(/* webpackChunkName: "it.localized" */ '@/localeWrapper/itWrapper');
const jaWrapper = () => import(/* webpackChunkName: "ja.localized" */ '@/localeWrapper/jaWrapper');
const koWrapper = () => import(/* webpackChunkName: "ko.localized" */ '@/localeWrapper/koWrapper');
const msWrapper = () => import(/* webpackChunkName: "ms.localized" */ '@/localeWrapper/msWrapper');
const ptWrapper = () => import(/* webpackChunkName: "pt.localized" */ '@/localeWrapper/ptWrapper');
const ruWrapper = () => import(/* webpackChunkName: "ru.localized" */ '@/localeWrapper/ruWrapper');
const thWrapper = () => import(/* webpackChunkName: "th.localized" */ '@/localeWrapper/thWrapper');
const zh_hantWrapper = () => import(/* webpackChunkName: "zhHant.localized" */ '@/localeWrapper/zh_hantWrapper');
const zh_hansWrapper = () => import(/* webpackChunkName: "zhHans.localized" */ '@/localeWrapper/zh_hansWrapper');

/**
 * Uncomment corresponding locale to enable Language Wrapper
 */
const localeWithWrapper = [
  'ar',
  'de',
  'en',
  'es',
  'fr',
  'id',
  'it',
  'ja',
  'ko',
  'ms',
  'pt',
  'ru',
  'th',
  'zh-hant', 
  'zh-hans'
];

/**
 * Start Setting Router
 */
Vue.use(Router);
Vue.component('router-link-localed', RouterLinkLocaled);

export function createRouter(i18n, context, previewProfile) {
  /**
   * Setting language specific library 
   * @param {String} lang 
   */
  const setLocale = (lang) => {
    i18n.locale = lang;
    axios.defaults.params = {};
    axios.defaults.params.lang = i18n.locale;
    if (global.document) {
      global.document.documentElement.lang = lang;
      global.document.documentElement.dir = supportLocale.find(({locale}) => locale == lang)['dir'] || 'ltr';
    }
  };

  const createLangRoute = (routes, lang) => routes.map((route) => {
    const newRoute = {};
    newRoute.name = route.name ? `${route.name}_${lang}` : route.name;
    newRoute.children = route.children ? createLangRoute(route.children, lang) : route.children;
    if (typeof route.redirect == 'string' && route.redirect.startsWith('/')) {
      newRoute.redirect = `/${lang}${route.redirect}`;
    }
    return Object.assign({}, route, newRoute);
  });

  const createLangRouter = (route) =>  supportLocale.filter(({disable}) => !disable)
    .map(({locale}) => {
      return {
        path: '/' + locale,
        beforeEnter: (to, from, next) => {
          console.log('routing: clr', to.path);
          let { path } = to;
          path = path.split('/');
          let locale = path[1];
          if (((path.length < 3) || (path.length == 3 && path[path.length - 1] == '')) && to.name != `Home_${locale}` ) {
            return next({
              query: to.query,
              hash: to.hash,
              name: `Home_${locale}`,
              replace: true
            })
          }

          if (i18n.locale == locale) { 
            return next();
          }
          
          setLocale(locale);
          next();
        },
        component: localeWithWrapper.includes(locale) 
          ? eval(locale.replace('-', '_') + 'Wrapper')
          : { 
            name: 'lang-wrapper', 
            render: function(h) {
              return <router-view/>
            }
          },
        children: createLangRoute(route, locale),
      }
    })

  /**
   * Create SightMapRouter
   */
  const createSightMapRouter = () => {   
    const makeRouter = (sightMapType, category, overivew=null, categoryPath=null) => {
      return [{
        path: categoryPath ? categoryPath: category,
        component: { 
          name: 'sight-map-wrapper',
          render(h) { 
            return h({
              render: function(h) {
                return <router-view/>
              },
              props: {
                sightMapType: {
                  type: String,
                  default: ''
                },
                category: {
                  type: String,
                  default: ''
                }
              }
            }, { 
              props: { sightMapType, category } 
            })
          } 
        },
        children: [
          overivew ? overivew : { 
            path: '', 
            name: `sight-map-overivew-${sightMapType}-${category}`,
            component: createSightMapOverviewView(sightMapType, category, categoryPath)
          },
          { 
            path: ':group', 
            beforeEnter: (to, from, next) => {
              let { group } = to.params;
              if (group == 'others' && category == 'suggested-tours') {
                return next({path: '/suggested-tours#others'})
              }
              next()
            },
            name: `sight-map-list-${sightMapType}-${category}`,
            component: createSightMapListView(sightMapType, category, categoryPath)
          },
          { 
            path: ':group/:entity', 
            name: `sight-map-single-${sightMapType}-${category}`,
            component: createSightMapSingleView(sightMapType, category, categoryPath)
          }
        ]
      }]
    }

    return {
      path: '',
      name: 'sight-map',
      component: SightMapView,
      beforeEnter: (to, from, next) => {
        console.log('routing: well');
        let { path } = to;
        path = path.split('/');
        let locale = path[1].toLowerCase();
        if (!isValidLang(locale)) {
          return next({
            query: to.query,
            hash: to.hash,
            name: 'LanguageRouter'
          });
        }
        
        if (((path.length < 3) || (path.length == 3 && path[path.length - 1] == '')) && to.name != `Home_${locale}` ) {
          return next({
            query: to.query,
            hash: to.hash,
            name: `Home_${locale}`,
            replace: true
          })
        }

        if (i18n.locale == locale) { 
          return next(); 
        }
        
        setLocale(locale);
        next();
      },
      children: [
        ...createLangRouter([{
          path: '',
          component: { 
            name: 'sight-map-wrapper',
            render(h) { 
              return h({
                render: function(h) {
                  return <router-view/>
                },
              })
            } 
          },
        }]),
        ...createLangRouter([
          {
            path: 'sight/:entity',
            component: SightView
          }
        ]),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.TOUR, 
          TOUR_CATEGORY.PLAN_YOUR_TRIP
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.TOUR, 
          TOUR_CATEGORY.EXPLORE_ON_YOUR_OWN_ROUTES
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.TOUR, 
          TOUR_CATEGORY.MLF,
          {
            path: '',
            beforeEnter: (to, from, next) => {
              let { path } = to;
              path = path.split('/');
              let locale = path[1];
              next({
                name: `MacaoLightFestival_${locale}`,
                // replace: true
              })
            },
          },
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.TOUR, 
          TOUR_CATEGORY.STEP_OUT_MACAO
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.TOUR, 
          TOUR_CATEGORY.MACAO_FULL_OF_FUN
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.EXPERIENCE, 
          EXPERIENCE_CATEGORY.SIGHTSEEING
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.EXPERIENCE, 
          EXPERIENCE_CATEGORY.ENTERTAINMENT
        )),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.EXPERIENCE, 
          EXPERIENCE_CATEGORY.SHOPPING
        )),
        ...createLangRouter(DiningSightMapRouter),
        ...createLangRouter(EventSightMapRouter),
        ...createLangRouter(AccommodationSightMapRouter),
        ...createLangRouter([
          {
            name: 'TagsMapView',
            path: 'tags/:tagId',
            component: TagView
          }
        ]),
        ...createLangRouter(makeRouter(
          SIGHT_MAP_TYPE.EXPERIENCE, 
          EXPERIENCE_CATEGORY.FNB,
          null,
          // 'dining/macao-classic-brand-and-distinctive-shops'
          'dining/specialty-foods'
        )),
      ]
    }
  } 

  const router = new Router({
    mode: 'history',
    scrollBehavior,
    routes: [
      createSightMapRouter(),
      {
        path: '/',
        component: {
          name: 'main-route-loading-placeholder-wrapper',
          render: function(h) {
            return <loading-view-placeholder>
              <router-view></router-view>
            </loading-view-placeholder>
          }
        },
        children: [
          ...createLangRouter([
            ...createMainRoutes(previewProfile),
            ...FooterArticleRouter,
            {
              path: 'macao-light-festival',
              name: `MacaoLightFestival`,
              component: CreateCustomArticle('mlf', '2019')
            },
            {
              path: 'chinese-new-year-parade',
              name: `ChineseNewYearParade`,
              component: CreateCustomArticle('cny', 'parade')
            },
            {
              path: '*',
              name: 'NotMatchFound',
              component: PageNotFoundView
            },
            {
              path: '*',
              meta: { replace: true },
              name: 'SomethingWrongKeepURL',
              component: WhoopsView,
            },
          ]),
        ]
      },
      {
        path: '/no-lang',
        alias: '*',
        name: 'LanguageRouter',
        beforeEnter: (to, from, next) => {
          // handle from sightmap
          const isDirect = to.path.indexOf('/no-lang') > -1;
          let lang;

          // check is from have lang
          let fromLang = from.path.split('/')[1].toLowerCase();

          let toLang = to.path.split('/')[1].toLowerCase();
          let toLangDisabled = isValidDisabledLang(toLang);

          console.log('routing: no-lang');
          if (isValidLang(fromLang) && !isDirect) {
            next({
              path: `/${fromLang}${to.fullPath}`, 
              replace: from.matched.some(record => record.meta.replace)
            });
            return;
          }

          if (context) {
            // server-side
            lang = getUserLang(context.lang, context.userAcceptLang);
          } else {
            // client-side
            const langInCookie = Cookies.get('lang');
            const acceptLang = window && window.navigator && window.navigator.language;
            lang = getUserLang(langInCookie, acceptLang);
          }

          // set lang to app
          setLocale(lang);
          if (isDirect) {
            next({
              query: to.query,
              hash: to.hash,
              name: `Home_${lang}`,
              replace: true
            });
          } else {
            next({
              path: toLangDisabled ? `/${lang}/${to.fullPath.split('/').slice(2).join('/')}` : `/${lang}${to.fullPath}`,
              replace: true
            });
          }
        },
        component: {
          render: function() {
            return <div>This Message SHOULD NOT be Show</div>
          },
        },
      },
    ],
  });

  router.beforeEach((to, from, next) => {
    // 全站統一：如果 URL query 的 keyword 有前後空白，直接 trim 後用 replace 導向乾淨的網址
    // 例：...?keyword=%20888 -> ...?keyword=888（不新增 history entry，避免返回鍵卡住）
    const raw = to && to.query ? to.query.keyword : undefined;
    if (typeof raw !== 'string') {
      return next();
    }

    const trimmed = raw.trim();
    if (trimmed === raw) {
      return next();
    }

    const nextQuery = { ...(to.query || {}) };
    if (trimmed) {
      nextQuery.keyword = trimmed;
    } else {
      // 如果 trim 後是空字串，移除 keyword，避免出現 ?keyword=
      delete nextQuery.keyword;
    }

    return next({
      name: to.name,
      params: to.params,
      query: nextQuery,
      hash: to.hash,
      replace: true,
    });
  });
  
  return legacyNavigation(router);
};
